/**
 * Vue composable that detects the Gonka Keeper extension and exposes a
 * stable reference to its injected provider.
 *
 * Detection logic is identical to `components/ConnectWallet.vue` — both
 * paths must match Keeper's PROVIDER_INFO in
 * `wallets/gonka-keeper/src/provider/inpage.ts`:
 *   (a) EIP-6963 `gonka:announceProvider` event channel, matched by
 *       uuid + rdns. Race-free across multiple cosmos wallets.
 *   (b) Synchronous probe of `window.gonkaKeeper` at MOUNT TIME, guarded
 *       by `isGonkaKeeper === true && providerId === 'gonka-keeper'`.
 *       Needed for Keeper builds ≤ 0.1.6 that don't emit the announce
 *       event.
 * The provider reference is captured ONCE per mount; never re-read at
 * call time, so a sibling extension's late `defineProperty` cannot swap
 * the target between mount and the user's click.
 *
 * The duplication with ConnectWallet.vue is intentional for T12 — see
 * task file: refactoring ConnectWallet to use this composable is a
 * follow-up, not part of this commit.
 */
import { onBeforeUnmount, onMounted, ref, shallowRef, type Ref, type ShallowRef } from 'vue';

const KEEPER_UUID = 'e88a3b1c-6c4f-4b1a-9a3f-d76e87f5d39e';
const KEEPER_RDNS = 'ai.gonka.keeper';

export type KeeperStatus = 'detecting' | 'found' | 'missing';

export interface UseKeeperProvider {
  /** ShallowRef so Vue's deep-reactivity proxy doesn't break method `this` binding. */
  provider: ShallowRef<GonkaProvider | null>;
  status: Ref<KeeperStatus>;
}

function probeIdentifiedKeeper(): GonkaProvider | null {
  if (typeof window === 'undefined') return null;
  const candidate = window.gonkaKeeper as GonkaProvider | undefined;
  if (!candidate) return null;
  const flags = candidate as unknown as { isGonkaKeeper?: unknown; providerId?: unknown };
  if (flags.isGonkaKeeper !== true) return null;
  if (flags.providerId !== 'gonka-keeper') return null;
  return candidate;
}

export function useKeeperProvider(): UseKeeperProvider {
  const provider = shallowRef<GonkaProvider | null>(null);
  const status = ref<KeeperStatus>('detecting');

  let detach: (() => void) | null = null;

  onMounted(() => {
    let settled = false;
    let timeoutId: number | null = null;

    const settle = (state: 'found' | 'missing'): void => {
      if (settled) return;
      settled = true;
      status.value = state;
      cleanup();
    };

    const onAnnounce = (e: CustomEvent<GonkaAnnounceEventDetail>): void => {
      const detail = e.detail;
      if (!detail || !detail.info || !detail.provider) return;
      if (detail.info.uuid !== KEEPER_UUID || detail.info.rdns !== KEEPER_RDNS) return;
      provider.value = detail.provider;
      settle('found');
    };

    const probeAndSettle = (): void => {
      const p = probeIdentifiedKeeper();
      if (p) {
        provider.value = p;
        settle('found');
      }
    };

    const onInitialized = (): void => probeAndSettle();

    const cleanup = (): void => {
      window.removeEventListener('gonka:announceProvider', onAnnounce as EventListener);
      window.removeEventListener('gonkaKeeper#initialized', onInitialized);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
    detach = cleanup;

    window.addEventListener('gonka:announceProvider', onAnnounce as EventListener);
    window.dispatchEvent(new CustomEvent('gonka:requestProvider'));
    window.addEventListener('gonkaKeeper#initialized', onInitialized);
    probeAndSettle();

    timeoutId = window.setTimeout(() => settle('missing'), 2000);
  });

  onBeforeUnmount(() => {
    if (detach) detach();
  });

  return { provider, status };
}
